import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import apiService from './ApiService';
import resolveHubBaseUrl from '../utils/hubBaseUrl';

// Conexión única de presencia en línea. Mientras está abierta, el backend considera
// al usuario "en línea". Se abre al iniciar sesión y se cierra al salir o ante un 401.

const START_RETRY_MS = 15000;
const MAX_RECONNECT_DELAY_MS = 30000;

const getHubBaseUrl = () =>
  resolveHubBaseUrl();

class PresenceClient {
  constructor() {
    this.connection = null;
    this.generation = 0;
    this.wanted = false;
    this.retryTimer = null;
    this.changeListeners = new Set();
    this.statusListeners = new Set();
    this.status = 'disconnected';
  }

  setStatus(status) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => {
      try {
        listener(status);
      } catch (err) {
        console.warn('Error en listener de estado de presencia', err);
      }
    });
  }

  getStatus() {
    return this.status;
  }

  async start() {
    this.wanted = true;
    await this.teardown();
    const generation = ++this.generation;

    if (!apiService.getTokenFromCookies()) {
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(`${getHubBaseUrl()}/hubs/presencia?app=web`, {
        accessTokenFactory: () => apiService.getTokenFromCookies() || '',
        withCredentials: true
      })
      .withAutomaticReconnect({
        // Reintenta indefinidamente mientras haya sesión; sin token deja de insistir.
        nextRetryDelayInMilliseconds: (context) =>
          apiService.getTokenFromCookies()
            ? Math.min(MAX_RECONNECT_DELAY_MS, 2000 * (context.previousRetryCount + 1))
            : null
      })
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('PresenceChanged', (payload) => {
      this.changeListeners.forEach((listener) => {
        try {
          listener(payload);
        } catch (err) {
          console.warn('Error en listener de presencia', err);
        }
      });
    });
    connection.onreconnecting(() => {
      if (generation === this.generation) this.setStatus('reconnecting');
    });
    connection.onreconnected(async () => {
      if (generation !== this.generation) return;
      this.setStatus('connected');
      await this.syncAdminGroup();
    });
    connection.onclose(() => {
      if (generation !== this.generation) return;
      this.setStatus('disconnected');
      this.scheduleRetry();
    });

    this.connection = connection;
    this.setStatus('reconnecting');

    try {
      await connection.start();
      if (generation !== this.generation) {
        connection.stop().catch(() => {});
        return;
      }
      this.setStatus('connected');
      await this.syncAdminGroup();
    } catch (err) {
      if (generation !== this.generation) return;
      console.warn('No se pudo conectar al hub de presencia', err);
      this.setStatus('disconnected');
      this.scheduleRetry();
    }
  }

  scheduleRetry() {
    if (!this.wanted || this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.wanted && apiService.getTokenFromCookies()) {
        this.start();
      }
    }, START_RETRY_MS);
  }

  async teardown() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.generation += 1;
    const connection = this.connection;
    this.connection = null;
    if (connection) {
      try {
        await connection.stop();
      } catch (err) {
        // La conexión ya estaba cerrada.
      }
    }
    this.setStatus('disconnected');
  }

  async stop() {
    this.wanted = false;
    await this.teardown();
  }

  async syncAdminGroup() {
    const connection = this.connection;
    if (!connection || connection.state !== HubConnectionState.Connected) return;
    if (this.changeListeners.size === 0) return;
    try {
      await connection.invoke('JoinAdminPresence');
    } catch (err) {
      console.warn('No se pudo suscribir a los cambios de presencia', err);
    }
  }

  // Sólo para admin general: recibe un aviso cada vez que cambia la presencia.
  subscribeToChanges(listener) {
    this.changeListeners.add(listener);
    this.syncAdminGroup();
    return () => {
      this.changeListeners.delete(listener);
      const connection = this.connection;
      if (
        this.changeListeners.size === 0 &&
        connection &&
        connection.state === HubConnectionState.Connected
      ) {
        connection.invoke('LeaveAdminPresence').catch(() => {});
      }
    };
  }

  subscribeToStatus(listener) {
    this.statusListeners.add(listener);
    return () => {
      this.statusListeners.delete(listener);
    };
  }
}

const presenceClient = new PresenceClient();

export default presenceClient;
